import { useAtomValue } from 'jotai';
import { type CSSProperties, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@optiaxiom/react';

import {
  stickyboardDrawerModeAtom,
  stickyboardDrawerVisibleAtom,
  stickyboardDrawerWidthAtom,
} from '@frontend/atoms/stickyboardAtoms';
import { AssumedVAUBanner } from '@frontend/components/AssumedVAUBanner';
import { SidebarSlotContext, useSlotNode } from '@frontend/components/sidebar/sidebarSlot';
import { RUNTIME_ENV } from '@frontend/envConfig';
import { OpalChatOverlay } from '@frontend/features/chat/components/OpalChatOverlay';
import { LeftNavbar } from '@frontend/features/leftNavbar';
import {
  useNotificationFavicon,
  useNotificationTitle,
} from '@frontend/features/notifications/hooks';
import { StickyboardDrawer } from '@frontend/features/stickyboard/components/StickyboardDrawer';
import { useRouteDocumentTitle } from '@frontend/hooks/useDocumentTitle';
import { useFullstoryPageContext } from '@frontend/hooks/useFullstoryPageContext';
import { useNotificationEvents } from '@frontend/hooks/useNotificationEvents';
import { useOpalUser } from '@frontend/hooks/useOpalUser';

import { baseLayoutStyles, scrollAreaStyles, stageStyles } from './BaseLayout.css';
import { RightSidebarHost } from './RightSidebarHost';

export function BaseLayout() {
  const { opalUser } = useOpalUser();
  useRouteDocumentTitle();
  useFullstoryPageContext();
  useNotificationEvents();
  useNotificationFavicon();
  useNotificationTitle();

  useEffect(() => {
    if (!opalUser) {
      return;
    }

    if (RUNTIME_ENV === 'production' || RUNTIME_ENV === 'integration') {
      void import(/* webpackChunkName: "Userflow" */ '@frontend/userflow').then(
        ({ default: Userflow }) => {
          Userflow.init(opalUser, opalUser.userflow_token);
        }
      );
    }
  }, [opalUser]);

  const { node: stageNode, ref: stageRef } = useSlotNode();

  // When the Stickyboard drawer is docked ("held"), shift the content right to
  // fit it. In a flex column with stretch, a child's left margin shrinks it, so
  // the scroll area both narrows and shifts by the drawer width. "peek" floats
  // over the content instead, leaving the margin at 0.
  const drawerVisible = useAtomValue(stickyboardDrawerVisibleAtom);
  const drawerMode = useAtomValue(stickyboardDrawerModeAtom);
  const drawerWidth = useAtomValue(stickyboardDrawerWidthAtom);
  const contentMarginLeft = drawerVisible && drawerMode === 'held' ? drawerWidth : 0;

  // Measure the content stage so the Stickyboard drawer can render as a fixed
  // overlay aligned to it — outside the stage, above the chat MFE + chat
  // overlay (which the stage's `isolation` would otherwise trap it beneath).
  // Only observe when the drawer is visible — no point measuring otherwise.
  const [stageRect, setStageRect] = useState({ left: 0, top: 0, height: 0 });
  useEffect(() => {
    if (!stageNode || !drawerVisible) return;
    const update = () => {
      const r = stageNode.getBoundingClientRect();
      setStageRect({ left: r.left, top: r.top, height: r.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stageNode);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [stageNode, drawerVisible]);

  return (
    <SidebarSlotContext.Provider value={stageNode}>
      <Box className={baseLayoutStyles}>
        <LeftNavbar />
        <Box ref={stageRef} className={stageStyles}>
          <Box
            className={scrollAreaStyles}
            // CSS variable consumed by scrollAreaStyles for dynamic drawer margin
            style={{ '--stickyboard-content-margin': `${contentMarginLeft}px` } as CSSProperties}
          >
            <AssumedVAUBanner />
            <Outlet />
          </Box>
        </Box>
        <RightSidebarHost />
        <OpalChatOverlay />
        <StickyboardDrawer
          stageLeft={stageRect.left}
          stageTop={stageRect.top}
          stageHeight={stageRect.height}
        />
      </Box>
    </SidebarSlotContext.Provider>
  );
}
